import { Box, IconButton, Skeleton, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import type { FC } from "react"
import { twMerge } from "tailwind-merge"

import { WarningMessage } from "@/components/WarningMessage/WarningMessage"
import { DATE_FORMAT, UOM_RENDER_PRECISION, UOM_ROUNDING_PRECISION } from "@/constants/formats"
import { formatNumber } from "@/utils/number"
import { isNil } from "@/utils/utils"

dayjs.extend(utc)

export interface PriceUomDisplayProps {
  price: number | null
  priceUomLabel: string | null | undefined
  convertedPrice?: number | null
  convertedPriceUomLabel?: string | null
  total: number | null
  isFetching: boolean
  isManualPriceSet: boolean
  effectiveDate?: string | null
  disabled?: boolean
  onEdit?: () => void
  className?: string
  testIdSuffix?: string
}

export const PriceUomDisplay: FC<PriceUomDisplayProps> = ({
  price,
  priceUomLabel,
  convertedPrice,
  convertedPriceUomLabel,
  total,
  isFetching,
  isManualPriceSet,
  effectiveDate,
  disabled,
  onEdit,
  className,
  testIdSuffix,
}) => {
  const testId = (element: string) => `price-uom-display.${element}${testIdSuffix ? `-${testIdSuffix}` : ""}`

  return (
    <Box className={twMerge("gap-2", className)}>
      {isFetching ? (
        <Skeleton variant='text' width={150} />
      ) : (
        <>
          {isManualPriceSet && <WarningMessage message='Overridden' data-testid={testId("overridden")} />}
          {effectiveDate && (
            <Box className='mb-1'>
              <Typography variant='caption' className='text-green-900' data-testid={testId("effective-date")}>
                Effective {dayjs.utc(effectiveDate).format(DATE_FORMAT.US_SHORT)}
              </Typography>
            </Box>
          )}
          {!effectiveDate && (
            <Box className='mb-1'>
              <Typography variant='caption'></Typography>
            </Box>
          )}
        </>
      )}
      <div className='flex gap-2'>
        <Typography
          variant={isNil(price) ? "body2" : "h6"}
          className={isNil(price) ? "text-gray-700" : "font-bold text-gray-700"}
          data-testid={testId("price")}
        >
          {isFetching ? (
            <Skeleton variant='text' width={150} />
          ) : isNil(price) ? (
            "Not calculated"
          ) : (
            `$${formatNumber(price, UOM_ROUNDING_PRECISION)} /${priceUomLabel ?? ""}`
          )}
        </Typography>
        {!isFetching && onEdit && (
          <IconButton
            color='primary'
            className='p-0'
            size='small'
            disabled={disabled}
            aria-label='edit price per unit of measurement'
            onClick={onEdit}
            data-testid={testId("edit-button")}
          >
            <EditOutlined />
          </IconButton>
        )}
      </div>
      <Typography variant='caption' className='pt-2 text-gray-700' data-testid={testId("converted-price")}>
        {isFetching ? (
          <Skeleton variant='text' width={100} />
        ) : (
          !isNil(convertedPrice) &&
          `$${formatNumber(convertedPrice, UOM_ROUNDING_PRECISION)} /${convertedPriceUomLabel ?? ""}`
        )}
      </Typography>

      {(isFetching || !isNil(total)) && (
        <Typography variant='subtitle2' className='mt-3 text-gray-700' data-testid={testId("total")}>
          {isFetching || isNil(total) ? (
            <Skeleton variant='text' width={100} />
          ) : (
            <>Total: ${formatNumber(total, UOM_RENDER_PRECISION)}</>
          )}
        </Typography>
      )}
    </Box>
  )
}
